import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EmailBlockData } from '../ModernEmailBuilder';
import {
  PhotoIcon,
  DocumentTextIcon,
  CursorArrowRaysIcon,
  MinusIcon,
  ArrowsUpDownIcon,
  ShareIcon,
  CodeBracketIcon,
  ShoppingBagIcon,
  StarIcon,
  ListBulletIcon,
  TableCellsIcon,
  QueueListIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

interface LayoutTreePanelProps {
  blocks: EmailBlockData[];
  onUpdateBlocks: (newBlocks: EmailBlockData[]) => void;
  onSelectBlock: (blockId: string) => void;
  selectedBlockId: string | null;
  onUpdateBlock: (blockId: string, updates: Partial<EmailBlockData>) => void;
}


const HIcon: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`flex items-center justify-center font-bold text-[10px] leading-none ${className || ''}`} style={{ width: '1em', height: '1em' }}>H</div>
);

const PIcon: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`flex items-center justify-center font-bold text-[10px] leading-none ${className || ''}`} style={{ width: '1em', height: '1em' }}>P</div>
);

const getBlockIcon = (type: string) => {
  const iconClass = "w-4 h-4";
  switch (type) {
    case 'text':
    case 'paragraph':
    case 'quote':
    case 'caption':
      return <PIcon className={iconClass} />;
    case 'heading':
    case 'title':
    case 'subtitle':
      return <HIcon className={iconClass} />;
    case 'image':
    case 'logo':
      return <PhotoIcon className={iconClass} />;
    case 'button':
      return <CursorArrowRaysIcon className={iconClass} />;
    case 'columns':
      return <TableCellsIcon className={iconClass} />;
    case 'divider':
      return <MinusIcon className={iconClass} />;
    case 'spacer':
      return <ArrowsUpDownIcon className={iconClass} />;
    case 'social':
      return <LinkIcon className={iconClass} />;
    case 'list':
      return <ListBulletIcon className={iconClass} />;
    case 'code':
      return <CodeBracketIcon className={iconClass} />;
    case 'product':
    case 'product-rec':
      return <ShoppingBagIcon className={iconClass} />;
    case 'star-rating':
      return <StarIcon className={iconClass} />;
    default:
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      );
  }
};

const getBlockLabel = (block: EmailBlockData) => {
  if (block.content.text) {
    const text = block.content.text.toString().substring(0, 20);
    return text.length > 0 ? text + (block.content.text.toString().length > 20 ? '...' : '') : block.type;
  }
  if (block.type === 'image' && block.content.alt) {
    return block.content.alt;
  }
  return block.type.charAt(0).toUpperCase() + block.type.slice(1);
};

interface SortableTreeItemProps {
  block: EmailBlockData;
  index: number;
  isLast: boolean;
  depth: number;
  selectedBlockId: string | null;
  expandedIds: Set<string>;
  toggleExpand: (id: string, e: React.MouseEvent) => void;
  handleMove: (index: number, direction: 'up' | 'down', e: React.MouseEvent) => void;
  toggleVisibility: (block: EmailBlockData, e: React.MouseEvent) => void;
  onSelectBlock: (blockId: string) => void;
  renderTreeItem: (block: EmailBlockData, index: number, depth: number) => React.ReactNode;
}

const SortableTreeItem: React.FC<SortableTreeItemProps> = ({
  block,
  index,
  isLast,
  depth,
  selectedBlockId,
  expandedIds,
  toggleExpand,
  handleMove,
  toggleVisibility,
  onSelectBlock,
  renderTreeItem,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative' as const,
  };

  const isExpanded = expandedIds.has(block.id);
  const isSelected = selectedBlockId === block.id;
  const hasChildren = block.type === 'columns' && block.content.columnContent;

  return (
    <div ref={setNodeRef} style={style} className="flex flex-col">
      <div
        onClick={() => onSelectBlock(block.id)}
        className={`group flex items-center py-2 px-3 cursor-pointer transition-colors ${isSelected ? 'bg-ocean-50 text-ocean-600' : 'hover:bg-slate-50 text-slate-700'
          } ${isDragging ? 'opacity-50 ring-2 ring-ocean-200' : ''}`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="mr-2 p-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>

        {/* Expansion Arrow */}
        <div className="w-5 flex-shrink-0">
          {hasChildren && (
            <button
              onClick={(e) => toggleExpand(block.id, e)}
              className={`p-0.5 rounded hover:bg-slate-200 transition-transform ${isExpanded ? 'rotate-90' : ''
                }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {/* Icon */}
        <div className={`mr-2 flex-shrink-0 ${isSelected ? 'text-ocean-500' : 'text-slate-400'}`}>
          {getBlockIcon(block.type)}
        </div>

        {/* Label */}
        <span className="flex-1 text-xs font-medium truncate uppercase tracking-tight">
          {getBlockLabel(block)}
        </span>

        {/* Controls */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
          <button
            onClick={(e) => handleMove(index, 'up', e)}
            disabled={index === 0}
            className={`p-1 rounded hover:bg-slate-200 ${index === 0 ? 'text-slate-200' : 'text-slate-400'}`}
            title="Move Up"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={(e) => handleMove(index, 'down', e)}
            disabled={isLast}
            className={`p-1 rounded hover:bg-slate-200 ${isLast ? 'text-slate-200' : 'text-slate-400'}`}
            title="Move Down"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => toggleVisibility(block, e)}
            className={`p-1 rounded hover:bg-slate-200 ${block.hidden ? 'text-slate-300' : 'text-slate-400'}`}
            title={block.hidden ? 'Show' : 'Hide'}
          >
            {block.hidden ? (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 .594 0 1.171.051 1.731.147m3.992 1.159a10.042 10.042 0 012.458 2.392A10.042 10.042 0 0121.75 12c-1.274 4.057-5.064 7-9.542 7-.594 0-1.171-.051-1.731-.147m-3.992-1.159a10.042 10.042 0 01-2.458-2.392A10.042 10.042 0 012.25 12c1.274-4.057 5.064-7 9.542-7 .594 0 1.171.051 1.731.147M3 3l18 18" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Children (for Columns) */}
      {hasChildren && isExpanded && (
        <div className="flex flex-col">
          {(block.content.columnContent as (EmailBlockData[] | string)[]).map((col, colIndex) => (
            <div key={`${block.id}-col-${colIndex}`}>
              <div className="py-1 px-3 bg-slate-100/50 text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2" style={{ paddingLeft: `${depth * 16 + 40}px` }}>
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                Column {colIndex + 1}
              </div>
              {Array.isArray(col) && col.map((childBlock, childIndex) => (
                <div key={childBlock.id}>
                  {renderTreeItem(childBlock, childIndex, depth + 2)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const LayoutTreePanel: React.FC<LayoutTreePanelProps> = ({
  blocks,
  onUpdateBlocks,
  onSelectBlock,
  selectedBlockId,
  onUpdateBlock,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleMove = (index: number, direction: 'up' | 'down', e: React.MouseEvent) => {
    e.stopPropagation();
    if (direction === 'up' && index > 0) {
      onUpdateBlocks(arrayMove(blocks, index, index - 1));
    } else if (direction === 'down' && index < blocks.length - 1) {
      onUpdateBlocks(arrayMove(blocks, index, index + 1));
    }
  };

  const toggleVisibility = (block: EmailBlockData, e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdateBlock(block.id, { hidden: !block.hidden });
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onUpdateBlocks(arrayMove(blocks, oldIndex, newIndex));
      }
    }
  };

  const renderTreeItem = (block: EmailBlockData, index: number, depth: number = 0) => {
    return (
      <SortableTreeItem
        key={block.id}
        block={block}
        index={index}
        isLast={index === blocks.length - 1}
        depth={depth}
        selectedBlockId={selectedBlockId}
        expandedIds={expandedIds}
        toggleExpand={toggleExpand}
        handleMove={handleMove}
        toggleVisibility={toggleVisibility}
        onSelectBlock={onSelectBlock}
        renderTreeItem={renderTreeItem}
      />
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Layout</h3>
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">
          {blocks.length} Blocks
        </span>
      </div>
      <div className="flex-1 overflow-y-auto py-2 custom-scrollbar">
        {blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-slate-400 px-6 text-center">
            <svg className="w-8 h-8 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
            <p className="text-xs">No blocks yet. Drag some from the palette!</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={blocks.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col">
                {blocks.map((block, index) => renderTreeItem(block, index))}
              </div>
            </SortableContext>
            <DragOverlay adjustScale={false}>
              {activeId ? (
                <div className="bg-white border-2 border-ocean-500 rounded-lg shadow-xl opacity-90 scale-105 pointer-events-none overflow-hidden">
                  {(() => {
                    const activeBlock = blocks.find(b => b.id === activeId);
                    if (!activeBlock) return null;
                    return (
                      <div className="flex items-center py-3 px-4 bg-white">
                        <div className="mr-3 text-ocean-500">
                          {getBlockIcon(activeBlock.type)}
                        </div>
                        <span className="text-sm font-semibold uppercase tracking-tight text-slate-800">
                          {getBlockLabel(activeBlock)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </div>
  );
};
